import React from 'react';

import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import * as SelectPrimitive from '@radix-ui/react-select';

import { SearchTypeahead } from '../../SearchTypeahead/SearchTypeahead';
import { generateSelectItemTestId } from './SelectOption/generateSelectItemTestId';
import type { SelectOption } from './SelectOption/SelectOption';

const emptySelectOptionValue = 'empty_value';

// Utility to merge tailwind classes that might be added by the caller with our defaults
function mergeClassNames(...classes: (string | undefined | null | false)[]) {
  return classes.filter(Boolean).join(' ');
}

// Trigger
const SelectTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(function SelectTrigger({ className, ...props }, ref) {
  return (
    <SelectPrimitive.Trigger
      ref={ref}
      {...props}
      className={mergeClassNames(className ?? '', 'flex items-center')}
    />
  );
});

const SelectContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(function SelectContent({ className, ...props }, ref) {
  return (
    <SelectPrimitive.Content
      ref={ref}
      sideOffset={5}
      align="start"
      position="popper"
      {...props}
      className={mergeClassNames(
        className ?? '',
        'relative z-50 max-h-[30vh] w-[var(--radix-select-trigger-width)] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md bg-white shadow-lg'
      )}
    />
  );
});

const SelectViewport = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Viewport>
>(function SelectViewport({ className, ...props }, ref) {
  return (
    <SelectPrimitive.Viewport
      ref={ref}
      {...props}
      className={mergeClassNames(className ?? '', 'py-0')}
    />
  );
});

function SelectPortal({
  children,
  onOpenAutoFocus,
  ...props
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
  onOpenAutoFocus?: boolean;
}) {
  return (
    <SelectPrimitive.Portal data-testid="select-portal">
      <SelectContent {...props}>{children}</SelectContent>
    </SelectPrimitive.Portal>
  );
}

const SelectItem = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(function SelectItem({ className, ...props }, ref) {
  return (
    <SelectPrimitive.Item
      ref={ref}
      {...props}
      className={mergeClassNames(
        className ?? '',
        'relative flex cursor-default select-none items-center rounded-md py-2 pl-8 pr-4'
      )}
    />
  );
});

const SelectSeparator = (
  props: React.ComponentProps<typeof SelectPrimitive.Separator>
) => (
  <SelectPrimitive.Separator
    {...props}
    className={mergeClassNames(
      props.className ?? '',
      'my-1 h-px bg-primary-700'
    )}
  />
);

const SelectItemIndicator = (
  props: React.ComponentProps<typeof SelectPrimitive.ItemIndicator>
) => (
  <SelectPrimitive.ItemIndicator
    {...props}
    className={mergeClassNames(
      props.className ?? '',
      'absolute left-0 inline-flex w-6 items-center justify-center'
    )}
  />
);

const scrollButtonClass =
  'flex items-center justify-center h-6 bg-white text-primary-700 cursor-default';

const SelectScrollUpButton = (
  props: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>
) => (
  <SelectPrimitive.ScrollUpButton {...props} className={scrollButtonClass} />
);

const SelectScrollDownButton = (
  props: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>
) => (
  <SelectPrimitive.ScrollDownButton {...props} className={scrollButtonClass} />
);

export type SelectProps = SelectPrimitive.SelectProps & {
  id?: string;
  options: SelectOption[];
  onChange: (value: SelectOption | undefined) => void;
  selected?: SelectOption;
  placeholder?: string;
  testIdSuffix?: string;
  error?: string;
  query?: string;
  setQuery?: (query: string) => void;
  searchable?: boolean;
  contentWidth?: string;
};

export const Select = React.forwardRef(function _Select(
  {
    id,
    selected,
    options,
    onChange,
    placeholder = 'Select an option',
    testIdSuffix,
    error,
    query,
    setQuery,
    searchable = false,
    contentWidth,
    ...primitiveProps
  }: SelectProps,
  ref: React.Ref<HTMLButtonElement>
) {
  const baseClass = `whitespace-nowrap relative flex w-full cursor-default items-center justify-between gap-1 rounded-md border
   bg-white py-2 px-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75
   focus-visible:ring-offset-2 data-[disabled]:cursor-not-allowed data-[disabled]:text-gray-300 sm:text-sm`;

  const errorClass = error
    ? 'focus:border-red-500 focus-visible:border-red-500 border-red-300 focus-visible:ring-offset-red-300 '
    : 'focus:border-indigo-500 focus-visible:border-indigo-500 focus-visible:ring-offset-blue-300 border-gray-300';

  return (
    <SelectPrimitive.Root
      {...primitiveProps}
      onValueChange={(newValue) => {
        const needleForSelectOption =
          adaptNewRadixValueToSelectOptionValue(newValue);
        // SelectItem only accepts strings for value, so we adapt to SelectOption here
        const newSelectOption = options.find(
          (option) => option.value === needleForSelectOption
        );
        onChange(newSelectOption);
      }}
      // Again, adapting to SelectOption if provided and falling back to value
      value={
        selected?.value
          ? adaptSelectOptionValueToRadixValue(selected.value)
          : primitiveProps.value
      }
      data-testid="select"
    >
      <SelectTrigger
        id={id}
        aria-label={placeholder}
        className={`${baseClass} ${errorClass}`}
        ref={ref}
      >
        <SelectPrimitive.Value
          placeholder={placeholder}
          data-testid={
            testIdSuffix ? `select-value-${testIdSuffix}` : 'select-value'
          }
        />
        <SelectPrimitive.Icon data-testid="select-trigger">
          <ChevronDownIcon className="h-4 w-4 text-primary-700" />
        </SelectPrimitive.Icon>
      </SelectTrigger>
      <SelectPortal
        data-testid="select-content"
        style={contentWidth ? { width: contentWidth } : undefined}
        // Skip first focus when the select list is searchable, this should
        // prevent focus from getting stolen by the list when its filtered
        onOpenAutoFocus={searchable && typeof setQuery === 'function'}
      >
        {searchable && typeof setQuery === 'function' && (
          <div className="p-2" onKeyDown={(e) => e.stopPropagation()}>
            <SearchTypeahead
              value={query}
              onChange={setQuery}
              placeholder="Search..."
              hint="Search for options"
              dataTestId="multi-select-search"
            />
          </div>
        )}
        <SelectScrollUpButton>
          <ChevronUpIcon className="h-4 w-4" />
        </SelectScrollUpButton>
        <SelectViewport>
          {options.map((option, index) => {
            const valueForSelectItem = adaptSelectOptionValueToRadixValue(
              option.value
            );

            return option.value === 'select-separator' ? (
              <SelectSeparator key={`select-separator-${index}`} />
            ) : (
              <SelectItem
                key={`${option.value}-${index}`}
                value={valueForSelectItem}
                className="hover:bg-mint hover:text-white data-[highlighted]:bg-mint data-[disabled]:text-gray-300 data-[highlighted]:text-white"
              >
                <SelectPrimitive.ItemText
                  data-testid={generateSelectItemTestId(option.value)}
                >
                  {option.label}
                </SelectPrimitive.ItemText>
                <SelectItemIndicator>
                  <CheckIcon className="h-4 w-4" />
                </SelectItemIndicator>
              </SelectItem>
            );
          })}
        </SelectViewport>
        <SelectScrollDownButton>
          <ChevronDownIcon className="h-4 w-4" />
        </SelectScrollDownButton>
      </SelectPortal>
    </SelectPrimitive.Root>
  );
});

function adaptNewRadixValueToSelectOptionValue(newValue: string) {
  return newValue === emptySelectOptionValue ? '' : newValue;
}

function adaptSelectOptionValueToRadixValue(value: string) {
  return value === '' ? emptySelectOptionValue : value;
}
